import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { User } from './user.model';
import { Router } from '@angular/router';
import { ConfigAssetLoaderService } from '../config/config-asset-loader.service';
import * as Sentry from '@sentry/angular';
import { OAuthEvent, OAuthService, OAuthStorage } from 'angular-oauth2-oidc';
import { LOGGEDIN_MARKER_KEY } from './sso-custom-storage.service';
import posthog from 'posthog-js';

export const ACCESS_TOKEN_KEY = 'access_token';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private JWT_TOKEN_KEY = 'token';

  public authenticationEventObservable: Subject<boolean> =
    new Subject<boolean>();

  public currentUserSubject: BehaviorSubject<User> = new BehaviorSubject<User>(
    null
  );

  constructor(
    private httpClient: HttpClient,
    private configService: ConfigAssetLoaderService,
    private cookieService: CookieService,
    private router: Router,
    private oauthService: OAuthService,
    private authStorage: OAuthStorage
  ) {
    const user = this.parseToken(this.getToken());
    this.addUserDetailsToExternalServices(user);
    this.currentUserSubject = new BehaviorSubject<User>(user);
    //this.user=this.setUserFromToken();
  }

  //TODO remove this method and use getCurrentUser(), mocking by jest is difficult
  public get currentUser(): User {
    return this.currentUserSubject.value;
  }

  public getCurrentUser(): User {
    return this.currentUserSubject.value;
  }

  public getToken(): string {
    const accessToken = this.authStorage.getItem(ACCESS_TOKEN_KEY);
    if (accessToken) {
      return accessToken;
    } else {
      return null;
    }

    // const jwtCookie=this.cookieService.get(this.JWT_COOKIE_KEY);
    // if(jwtCookie){
    //   return jwtCookie;
    // }else{
    //   return null;
    // }
  }

  public setToken(token: string) {
    const user: User = this.parseToken(token);
    //const expCookie=this.getCookieExpirationDate(user);
    //this.cookieService.set(this.JWT_COOKIE_KEY,token,expCookie,'/');
    this.currentUserSubject.next(user);
    this.addUserDetailsToExternalServices(user);
  }

  public setUserFromToken(token: string) {
    const user: User = this.parseToken(token);
    //const expCookie=this.getCookieExpirationDate(user);
    //this.cookieService.set(this.JWT_COOKIE_KEY,token,expCookie,'/');
    this.currentUserSubject.next(user);
    this.addUserDetailsToExternalServices(user);
  }

  addUserDetailsToExternalServices(user: User) {
    //const scope = Sentry.getCurrentScope();
    if (user) {
      Sentry.setUser({
        id: user.id,
      });
      
      // Identify user in PostHog
      posthog.identify(
        user.preferred_username,
        {
          userType: user.UserType,
          companyId: user.companyId,
          groupAdmin: user.groupAdmin,
          userId: user.id,
        }
      );
    } else {
      Sentry.setUser(null);
      
      // Reset PostHog user identification
      posthog.reset();
    }
  }

  // public refreshToken(token : string){
  //   const jwtTokenFromCookie=this.cookieService.get(this.JWT_COOKIE_KEY);
  //   if(jwtTokenFromCookie !== token ){
  //     console.log('refresh token');
  //     // console.log('old token:'+jwtTokenFromCookie);
  //     // console.log('new token:'+token);
  //     this.setToken(token);
  //   }
  // }

  private parseToken(token: string): User {
    const helper = new JwtHelperService();
    if (token) {
      //localStorage.setItem(this.JWT_TOKEN_KEY,token);
      let user: User = helper.decodeToken(token);
      user.partialAuth = user?.AuthType === 'Partial';
      return user;
    }
    return null;
  }

  hasAccessToGTLevel1() {
    return (
      this.currentUser?.applications.indexOf('GlobalTrendsLevel1') > -1 ||
      this.currentUser?.applications.indexOf('GlobalTrendsLevel2') > -1 ||
      this.currentUser?.applications.indexOf('ALL') > -1
    );
  }

  hasAccessToGTLevel2() {
    return (
      this.currentUser?.applications.indexOf('GlobalTrendsLevel2') > -1 ||
      this.currentUser?.applications.indexOf('ALL') > -1
    );
  }

  redirectToPartialAuthUrl(partialAuthKey: string) {
    window.location.href =
      this.configService.getConfig().ssoIssuer +
      '/login-actions/action-token?key=' +
      partialAuthKey +
      '&client_id=cullen-client';
  }

  logoutWithRedirectUrl(redirectUrlAfterLogin: string) {
    this.oauthService.postLogoutRedirectUri =
      this.oauthService.redirectUri + redirectUrlAfterLogin;
    this.logout();
    this.oauthService.postLogoutRedirectUri = this.oauthService.redirectUri;
    //this.loginSso(redirectUrlAfterLogin);
    //this.oauthService.initLoginFlow(redirectUrlAfterLogin)
  }

  setupAuthEvents() {
    this.oauthService.events.subscribe(({ type }: OAuthEvent) => {
      console.log('Auth event type : ' + type);
      switch (type) {
        case 'token_received':
          this.setUserFromToken(this.getToken());
          this.authenticationEventObservable.next(true);
          // Only navigate based on state if the current window is NOT an iframe.
          // Silent refresh occurs in an iframe; thus, this condition prevents unwanted navigation.
          if (!window.self.frameElement) {
            const returnUrl = this.oauthService.state;
            if (returnUrl) {
              this.router
                .navigateByUrl(decodeURIComponent(returnUrl))
                .then(() => {
                  // Optionally, clear the state
                  this.oauthService.state = null;
                });
            }
          }
          break;
        case 'session_error':
          console.log('session error');
          break;
        case 'token_refresh_error':
          console.log('error in token refresh');
          break;
      }
    });
  }

  loginSso(redirectUrlAfterLogin: string) {
    this.oauthService.initLoginFlow(redirectUrlAfterLogin);

    // this.oauthService
    //   .loadDiscoveryDocumentAndLogin({ state: redirectUrlAfterLogin })
    //   .then((result) => {
    //     if (result) {
    //       console.log('load discovery and login success: ' + result);
    //       if (redirectUrlAfterLogin) {
    //         const decodedUrl = decodeURIComponent(this.oauthService.state);
    //         console.log(
    //           'Redirecting to original url after login: ' + decodedUrl
    //         );
    //         this.router.navigateByUrl(decodedUrl);
    //       }
    //     }
    //   });
  }

  loadDiscoveryDocument() {
    console.log('load discovery doc');
    this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      if (this.oauthService.hasValidAccessToken()) {
        console.log('user is already logged in');
      }
    });
  }

  logout(noRedirectToLogin: boolean = false) {
    //this.cookieService.delete(this.JWT_COOKIE_KEY,  ' / ' );
    this.oauthService.logOut(noRedirectToLogin);
    this.currentUserSubject.next(null);
    //localStorage.removeItem(this.JWT_TOKEN_KEY);
    this.addUserDetailsToExternalServices(null);

    //window.location.href = '/login';
  }

  isAuthenticated(): boolean {
    if (
      this.oauthService.hasValidAccessToken() &&
      this.oauthService.hasValidIdToken()
    ) {
      return true;
    } else {
      return false;
    }
  }

  silentRefreshToken(): Promise<OAuthEvent> {
    return this.oauthService.silentRefresh();
  }

  isAccessTokenCookiePresent(): boolean {
    const jwtCookie = this.cookieService.get(ACCESS_TOKEN_KEY);
    const existsToken: boolean = jwtCookie ? true : false;
    return existsToken;
  }

  isUserLoggedInPreviously(): boolean {
    const loggedIn = localStorage.getItem(LOGGEDIN_MARKER_KEY);
    return loggedIn && loggedIn === 'true' ? true : false;
  }

  // login(username: string, password: string,rememberMe : boolean) {

  //   //  const body = new URLSearchParams();
  //   //   body.set('username', username);
  //   //   body.set('password',password);
  //   //   body.set('rememberMe',rememberMe.toString());
  //   // const httpHeaders = new HttpHeaders({
  //   //     'Content-Type' : 'application/x-www-form-urlencoded',
  //   // });
  //   const loginBaseUrl=this.configService.getConfig().apiBaseUrl+'auth/login';
  //   return this.httpClient.post<JwtTokenResponse>(loginBaseUrl, {username,password,rememberMe})
  //       .pipe(tap(response => {

  //           if(response?.success){
  //             this.setUserFromToken(response.token);
  //           }
  //       }));
  // }

  forgotPassword(username: string, email: string) {
    const forgotPasswardUrl =
      this.configService.getConfig().apiBaseUrl + 'auth/forgotpassword';
    return this.httpClient.post<any>(forgotPasswardUrl, { username, email });
  }

  isCullenUser() {
    return this.currentUser?.UserType === 'CULLEN_USER';
  }

  //TODO remove rememberMe and use from the token
  private getCookieExpirationDate(user: User) {
    return !user || user.partialAuth || !user.persistentAuth
      ? null
      : new Date(user.exp * 1000);
  }

  makeString() {
    return Math.random().toString(20).substring(2, 11);
  }

  public getResetPasswordUrl() {
    return (
      this.configService.getConfig().ssoIssuer +
      '/login-actions/reset-credentials?client_id=cullen-client&tab_id=' +
      this.makeString()
    );
  }
}
